"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wordmark } from "@/components/logo";
import { DEFAULT_PLATFORM_ORIGIN, ORIGIN_STORAGE_KEY } from "@/lib/origin";

export function LoginForm() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [origin, setOrigin] = useState(DEFAULT_PLATFORM_ORIGIN);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const saved = window.localStorage.getItem(ORIGIN_STORAGE_KEY);
    fetch("/api/auth/me").then((response) => {
      if (cancelled) return;
      if (saved) setOrigin(saved);
      if (response.ok) router.replace("/capture");
    });
    return () => {
      cancelled = true;
    };
  }, [router]);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setPending(true);
    setError("");
    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, origin }),
    });
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    setPending(false);
    if (!response.ok) {
      setError(payload?.error || "Не удалось войти.");
      return;
    }
    window.localStorage.setItem(ORIGIN_STORAGE_KEY, origin.trim());
    router.push("/capture");
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-1 flex-col px-5 pb-8 pt-8">
      <h1 className="font-heading text-4xl leading-none">N1-input</h1>
      <Wordmark className="mt-3 h-7 w-auto self-start" />
      <p className="mt-3 text-base leading-6 text-muted-foreground">
        Снимите визитку, надиктуйте встречу или выберите карточку контакта. Запись разложится по контактам, организациям и связям.
      </p>
      <div className="mt-8 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="origin">Адрес сервера</Label>
          <Input
            id="origin"
            type="url"
            inputMode="url"
            autoCapitalize="none"
            autoCorrect="off"
            required
            value={origin}
            onChange={(event) => setOrigin(event.target.value)}
            className="h-12 bg-card px-3 text-base"
            placeholder="https://netapp.aesoft.co"
          />
          <p className="text-sm leading-5 text-muted-foreground">Та же платформа NetworkingOne. Можно указать другой адрес.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Почта</Label>
          <Input
            id="email"
            type="text"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="h-12 bg-card px-3 text-base"
            placeholder="you@company.ru"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Пароль</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="h-12 bg-card px-3 text-base"
          />
        </div>
      </div>
      {error ? <p className="mt-4 text-sm text-destructive">{error}</p> : null}
      <Button type="submit" disabled={pending} className="mt-6 h-12 text-base">
        {pending ? "Входим…" : "Войти"}
      </Button>
      <p className="mt-4 text-sm leading-5 text-muted-foreground">Пароль уходит только на указанный сервер и в приложении не хранится.</p>
    </form>
  );
}
